import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'rsuite';
import { canUndo, drawnCount, RESHUFFLE_SOURCE_ORDER, toggleReshuffleZone } from '../../../shared/playtest';
import HistoryPanel from './HistoryPanel';
import {
  SWEPT_TO_COMMAND,
  SWEPT_TO_LIBRARY,
  SWEPT_TOGGLE_LABEL,
  sweptToCommand,
} from './playtestReshuffle';
import { usePlaytestSessions } from './PlaytestSessionProvider';

const SPACING = { section: 12, row: 8 };

const styles = {
  section: { display: 'flex', flexDirection: 'column', gap: SPACING.section },
  row: { display: 'flex', flexWrap: 'wrap', gap: SPACING.row, alignItems: 'center' },
};

/**
 * Session-wide controls: undo, untap, reshuffle sources, the swept-command
 * destination, new game, history, and end session. The command zone is never a
 * reshuffle source; the toggle controls where command-zone cards go when a
 * reshuffle sweeps them.
 */
const PlaytestControls = ({ collectionId, session, onEndSession }) => {
  const { dispatchAction, undoLast } = usePlaytestSessions();
  const state = session.engine;
  const [historyOpen, setHistoryOpen] = useState(false);

  const dispatch = (action) => dispatchAction(collectionId, action);

  return (
    <div data-testid="playtest-controls" style={styles.section}>
      <div style={styles.row}>
        <span data-testid="top-counter-library">{`Library ${state.zones.library.length}`}</span>
        <span data-testid="top-counter-drawn">{`Drawn ${drawnCount(state)}`}</span>
        <span data-testid="top-counter-game">{`Game ${state.game}`}</span>
        <Button
          data-testid="undo-button"
          disabled={!canUndo(session.undo)}
          onClick={() => undoLast(collectionId)}
        >
          Undo
        </Button>
        <Button data-testid="untap-all" onClick={() => dispatch({ type: 'untapAll' })}>
          Untap all
        </Button>
        <Button data-testid="new-game" onClick={() => dispatch({ type: 'newGame' })}>
          New game
        </Button>
        <Button data-testid="history-open" onClick={() => setHistoryOpen(true)}>
          History
        </Button>
        <Button data-testid="playtest-end-session" onClick={onEndSession}>
          End session
        </Button>
      </div>

      <div style={styles.row}>
        <span>Reshuffle sources:</span>
        {RESHUFFLE_SOURCE_ORDER.map((zone) => (
          <label key={zone}>
            <input
              type="checkbox"
              data-testid={`reshuffle-source-${zone}`}
              checked={state.reshuffleFrom.includes(zone)}
              onChange={() =>
                dispatch({
                  type: 'setReshuffleSources',
                  zones: toggleReshuffleZone(state.reshuffleFrom, zone),
                })
              }
            />
            {` ${zone}`}
          </label>
        ))}
      </div>

      <label style={styles.row}>
        <input
          type="checkbox"
          data-testid="swept-command-toggle"
          checked={sweptToCommand(state.sweptCommandCardsTo)}
          onChange={(event) =>
            dispatch({
              type: 'setSweptCommandDestination',
              to: event.target.checked ? SWEPT_TO_COMMAND : SWEPT_TO_LIBRARY,
            })
          }
        />
        {SWEPT_TOGGLE_LABEL}
      </label>

      {historyOpen ? (
        <Modal open onClose={() => setHistoryOpen(false)} size="md">
          <Modal.Header>
            <Modal.Title>History</Modal.Title>
          </Modal.Header>
          <Modal.Body data-testid="history-dialog">
            <HistoryPanel session={session} />
          </Modal.Body>
        </Modal>
      ) : null}
    </div>
  );
};

PlaytestControls.propTypes = {
  collectionId: PropTypes.string.isRequired,
  session: PropTypes.shape({
    engine: PropTypes.object.isRequired,
    undo: PropTypes.array.isRequired,
    cardViews: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
  onEndSession: PropTypes.func.isRequired,
};

export default PlaytestControls;
